import { useState } from "react"
import { Button } from "@/modules/shared/ui/components/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/modules/shared/ui/components/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/modules/shared/ui/components/select"
import { DocumentDto } from "../../entities/DocumentDto"
import { useFolderList } from "../../hooks/useFolders"
import { useMoveDocument } from "../../hooks/useDocuments"

const ROOT_SENTINEL = "__root__"

interface MoveDocumentModalProps {
  document: DocumentDto
  onClose: () => void
}

export function MoveDocumentModal({
  document,
  onClose,
}: MoveDocumentModalProps) {
  const [selectedFolderId, setSelectedFolderId] = useState<string>(
    document.folderId ?? ROOT_SENTINEL,
  )

  const { data: folders = [], isLoading: foldersLoading } = useFolderList()
  const { mutateAsync: moveDocument, isPending } = useMoveDocument()

  const handleConfirm = async () => {
    await moveDocument({
      documentId: document.id,
      folderId: selectedFolderId === ROOT_SENTINEL ? null : selectedFolderId,
    })
    onClose()
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Move Document</DialogTitle>
          <DialogDescription className="truncate">
            {document.fileName}
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          <Select
            value={selectedFolderId}
            onValueChange={setSelectedFolderId}
            disabled={foldersLoading}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a folder..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ROOT_SENTINEL}>
                — Root (no folder) —
              </SelectItem>
              {folders.map((folder) => (
                <SelectItem key={folder.id} value={folder.id}>
                  {folder.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isPending || foldersLoading}
          >
            {isPending ? "Moving..." : "Move"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
